import React from 'react';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import PlaylistAdd from '@mui/icons-material/PlaylistAdd';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HighlightOff from '@mui/icons-material/HighlightOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Tooltip, { TooltipProps } from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import RemoveCircleOutline from '@mui/icons-material/RemoveCircleOutline';
import AssignmentTurnedIn from '@mui/icons-material/AssignmentTurnedIn';
import PointOfSaleWithDollarSignOutline from '../../../customIcons/PointOfSaleWithDollarSignOutline';

export enum DataGridActionsMenuItems {
  Deactivate = 'deactivate',
  Duplicate = 'duplicate',
  Activate = 'activate',
  Delete = 'delete',
  Edit = 'edit',
  Approve = 'approve',
  Declare = 'declare',
  Remove = 'remove',
  Clone = 'clone',
  CreatePO = 'createPO',
  EditPO = 'editPO',
  SetAsPrimary = 'setAsPrimary',
  MarkAsComplete = 'markAsComplete',
}

export type CustomActionsMenuCellProps = {
  id?: string;
  actions: DataGridActionsMenuItems[];
  onClickFunctions?: Partial<Record<DataGridActionsMenuItems, () => void>>;
  buttonTitles?: Partial<Record<DataGridActionsMenuItems, string>>;
  iconOverrides?: Partial<Record<DataGridActionsMenuItems, React.ReactNode>>;
  tooltipTitles?: Partial<Record<DataGridActionsMenuItems, string>>;
  extraPermissions?: Partial<Record<DataGridActionsMenuItems, boolean>>;
  disabled?: Partial<Record<DataGridActionsMenuItems, boolean>>;
  isActive?: boolean;
};

function CustomActionsMenuCell(props: CustomActionsMenuCellProps) {
  const {
    id,
    isActive,
    actions,
    onClickFunctions,
    buttonTitles,
    iconOverrides,
    tooltipTitles,
    extraPermissions,
    disabled,
  } = props;
  const { t } = useTranslation();

  // menu
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // custom actions
  const tooltipProps: Partial<TooltipProps> = {
    placement: 'left',
    arrow: true,
    slotProps: {
      popper: {
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, -10],
            },
          },
        ],
      },
    },
  };

  const getPermissionValue = (key: DataGridActionsMenuItems) => {
    if (extraPermissions) {
      if (Object.keys(extraPermissions).includes(key)) {
        return extraPermissions[key];
      }
      return true;
    }
    return true;
  };

  const isDisabled = (key: DataGridActionsMenuItems) => {
    if (disabled) {
      if (Object.keys(disabled).includes(key)) {
        return disabled[key];
      }
    }
    return false;
  };

  const onClick = (key: DataGridActionsMenuItems) => {
    if (onClickFunctions) {
      onClickFunctions[key]?.();
      handleClose();
    }
  };

  const getTooltipTitle = (key: DataGridActionsMenuItems) => {
    if (tooltipTitles && Object.keys(tooltipTitles).includes(key)) {
      return tooltipTitles[key];
    }
    return undefined;
  };

  const actionOptions = actions
    .filter((action) => getPermissionValue(action))
    .map((action) => {
      if (action === DataGridActionsMenuItems.Edit) {
        return (
          <Tooltip {...tooltipProps} title={getTooltipTitle(DataGridActionsMenuItems.Edit)} key={action}>
            <MenuItem
              data-testid={`${id}-edit-btn`}
              onClick={() => onClick(DataGridActionsMenuItems.Edit)}
              disabled={isDisabled(DataGridActionsMenuItems.Edit)}
            >
              <ListItemIcon>
                {iconOverrides && iconOverrides[DataGridActionsMenuItems.Edit] ? (
                  iconOverrides[DataGridActionsMenuItems.Edit]
                ) : (
                  <EditIcon fontSize='small' />
                )}
              </ListItemIcon>
              <ListItemText>
                {buttonTitles && buttonTitles[DataGridActionsMenuItems.Edit]
                  ? buttonTitles[DataGridActionsMenuItems.Edit]
                  : t('buttonText.edit')}
              </ListItemText>
            </MenuItem>
          </Tooltip>
        );
      }

      if (action === DataGridActionsMenuItems.Clone) {
        return (
          <Tooltip {...tooltipProps} title={getTooltipTitle(DataGridActionsMenuItems.Clone)} key={action}>
            <MenuItem
              data-testid={`${id}-clone-btn`}
              onClick={() => onClick(DataGridActionsMenuItems.Clone)}
              disabled={isDisabled(DataGridActionsMenuItems.Clone)}
            >
              <ListItemIcon>
                {iconOverrides && iconOverrides[DataGridActionsMenuItems.Clone] ? (
                  iconOverrides[DataGridActionsMenuItems.Clone]
                ) : (
                  <ContentCopyIcon fontSize='small' />
                )}
              </ListItemIcon>
              <ListItemText>
                {buttonTitles && buttonTitles[DataGridActionsMenuItems.Clone]
                  ? buttonTitles[DataGridActionsMenuItems.Clone]
                  : t('buttonText.clone')}
              </ListItemText>
            </MenuItem>
          </Tooltip>
        );
      }

      if (action === DataGridActionsMenuItems.Duplicate) {
        return (
          <Tooltip {...tooltipProps} title={getTooltipTitle(DataGridActionsMenuItems.Duplicate)} key={action}>
            <MenuItem
              data-testid={`${id}-duplicate-btn`}
              onClick={() => onClick(DataGridActionsMenuItems.Duplicate)}
              disabled={isDisabled(DataGridActionsMenuItems.Duplicate)}
            >
              <ListItemIcon>
                {iconOverrides && iconOverrides[DataGridActionsMenuItems.Duplicate] ? (
                  iconOverrides[DataGridActionsMenuItems.Duplicate]
                ) : (
                  <PlaylistAdd fontSize='small' />
                )}
              </ListItemIcon>
              <ListItemText>
                {buttonTitles && buttonTitles[DataGridActionsMenuItems.Duplicate]
                  ? buttonTitles[DataGridActionsMenuItems.Duplicate]
                  : t('buttonText.duplicate')}
              </ListItemText>
            </MenuItem>
          </Tooltip>
        );
      }

      if (action === DataGridActionsMenuItems.Activate && !isActive) {
        return (
          <Tooltip {...tooltipProps} title={getTooltipTitle(DataGridActionsMenuItems.Activate)} key={action}>
            <MenuItem
              data-testid={`${id}-activate-btn`}
              onClick={() => onClick(DataGridActionsMenuItems.Activate)}
              disabled={isDisabled(DataGridActionsMenuItems.Activate)}
            >
              <ListItemIcon>
                {iconOverrides && iconOverrides[DataGridActionsMenuItems.Activate] ? (
                  iconOverrides[DataGridActionsMenuItems.Activate]
                ) : (
                  <CheckCircleIcon fontSize='small' />
                )}
              </ListItemIcon>
              <ListItemText>
                {buttonTitles && buttonTitles[DataGridActionsMenuItems.Activate]
                  ? buttonTitles[DataGridActionsMenuItems.Activate]
                  : t('buttonText.activate')}
              </ListItemText>
            </MenuItem>
          </Tooltip>
        );
      }

      if (action === DataGridActionsMenuItems.Deactivate && isActive) {
        const isDisabledButton = isDisabled(DataGridActionsMenuItems.Deactivate);
        return (
          <Tooltip {...tooltipProps} title={getTooltipTitle(DataGridActionsMenuItems.Deactivate)} key={action}>
            {/* Surrounding with a span because https://mui.com/material-ui/react-tooltip/#disabled-elements */}
            <span data-testid={`${id}-deactivate-tooltip-span`}>
              <MenuItem
                data-testid={`${id}-deactivate-btn`}
                onClick={() => !isDisabledButton && onClick(DataGridActionsMenuItems.Deactivate)}
                disabled={isDisabledButton}
              >
                <ListItemIcon>
                  {iconOverrides && iconOverrides[DataGridActionsMenuItems.Deactivate] ? (
                    iconOverrides[DataGridActionsMenuItems.Deactivate]
                  ) : (
                    <HighlightOff fontSize='small' />
                  )}
                </ListItemIcon>
                <ListItemText>
                  {buttonTitles && buttonTitles[DataGridActionsMenuItems.Deactivate]
                    ? buttonTitles[DataGridActionsMenuItems.Deactivate]
                    : t('buttonText.deactivate')}
                </ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
        );
      }

      if (action === DataGridActionsMenuItems.Delete) {
        const isDisabledButton = isDisabled(DataGridActionsMenuItems.Delete);
        return (
          <Tooltip {...tooltipProps} title={getTooltipTitle(DataGridActionsMenuItems.Delete)} key={action}>
            {/* Surrounding with a span because https://mui.com/material-ui/react-tooltip/#disabled-elements */}
            <span>
              <MenuItem
                data-testid={`${id}-delete-btn`}
                onClick={() => !isDisabledButton && onClick(DataGridActionsMenuItems.Delete)}
                disabled={isDisabledButton}
              >
                <ListItemIcon>
                  {iconOverrides && iconOverrides[DataGridActionsMenuItems.Delete] ? (
                    iconOverrides[DataGridActionsMenuItems.Delete]
                  ) : (
                    <DeleteIcon fontSize='small' />
                  )}
                </ListItemIcon>
                <ListItemText>
                  {buttonTitles && buttonTitles[DataGridActionsMenuItems.Delete]
                    ? buttonTitles[DataGridActionsMenuItems.Delete]
                    : t('buttonText.delete')}
                </ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
        );
      }

      if (action === DataGridActionsMenuItems.Approve) {
        return (
          <Tooltip {...tooltipProps} title={getTooltipTitle(DataGridActionsMenuItems.Approve)} key={action}>
            {/* Surrounding with a span because https://mui.com/material-ui/react-tooltip/#disabled-elements */}
            <span>
              <MenuItem
                data-testid={`${id}-approve-btn`}
                onClick={() => onClick(DataGridActionsMenuItems.Approve)}
                disabled={isDisabled(DataGridActionsMenuItems.Approve)}
              >
                <ListItemIcon>
                  {iconOverrides && iconOverrides[DataGridActionsMenuItems.Approve] ? (
                    iconOverrides[DataGridActionsMenuItems.Approve]
                  ) : (
                    <CheckCircleOutline fontSize='small' />
                  )}
                </ListItemIcon>
                <ListItemText>
                  {buttonTitles && buttonTitles[DataGridActionsMenuItems.Approve]
                    ? buttonTitles[DataGridActionsMenuItems.Approve]
                    : t('buttonText.approve')}
                </ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
        );
      }

      if (action === DataGridActionsMenuItems.Declare) {
        return (
          <Tooltip {...tooltipProps} title={getTooltipTitle(DataGridActionsMenuItems.Declare)} key={action}>
            <MenuItem
              data-testid={`${id}-declare-btn`}
              onClick={() => onClick(DataGridActionsMenuItems.Declare)}
              disabled={isDisabled(DataGridActionsMenuItems.Declare)}
            >
              <ListItemIcon>
                {iconOverrides && iconOverrides[DataGridActionsMenuItems.Declare] ? (
                  iconOverrides[DataGridActionsMenuItems.Declare]
                ) : (
                  <PointOfSaleWithDollarSignOutline />
                )}
              </ListItemIcon>
              <ListItemText>
                {buttonTitles && buttonTitles[DataGridActionsMenuItems.Declare]
                  ? buttonTitles[DataGridActionsMenuItems.Declare]
                  : t('buttonText.declare')}
              </ListItemText>
            </MenuItem>
          </Tooltip>
        );
      }

      if (action === DataGridActionsMenuItems.Remove) {
        return (
          <Tooltip {...tooltipProps} title={getTooltipTitle(DataGridActionsMenuItems.Remove)} key={action}>
            <MenuItem
              data-testid={`${id}-remove-btn`}
              onClick={() => onClick(DataGridActionsMenuItems.Remove)}
              disabled={isDisabled(DataGridActionsMenuItems.Remove)}
            >
              <ListItemIcon>
                {iconOverrides && iconOverrides[DataGridActionsMenuItems.Remove] ? (
                  iconOverrides[DataGridActionsMenuItems.Remove]
                ) : (
                  <RemoveCircleOutline fontSize='small' />
                )}
              </ListItemIcon>
              <ListItemText>
                {buttonTitles && buttonTitles[DataGridActionsMenuItems.Remove]
                  ? buttonTitles[DataGridActionsMenuItems.Remove]
                  : t('buttonText.remove')}
              </ListItemText>
            </MenuItem>
          </Tooltip>
        );
      }

      if (action === DataGridActionsMenuItems.CreatePO) {
        return (
          <Tooltip {...tooltipProps} title={getTooltipTitle(DataGridActionsMenuItems.CreatePO)} key={action}>
            <MenuItem
              data-testid={`${id}-create-po-btn`}
              onClick={() => onClick(DataGridActionsMenuItems.CreatePO)}
              disabled={isDisabled(DataGridActionsMenuItems.CreatePO)}
            >
              <ListItemText>
                {buttonTitles && buttonTitles[DataGridActionsMenuItems.CreatePO]
                  ? buttonTitles[DataGridActionsMenuItems.CreatePO]
                  : t('buttonText.createPO')}
              </ListItemText>
            </MenuItem>
          </Tooltip>
        );
      }

      if (action === DataGridActionsMenuItems.SetAsPrimary) {
        return (
          <Tooltip {...tooltipProps} title={getTooltipTitle(DataGridActionsMenuItems.SetAsPrimary)} key={action}>
            <MenuItem
              data-testid={`${id}-set-as-primary-btn`}
              onClick={() => onClick(DataGridActionsMenuItems.SetAsPrimary)}
              disabled={isDisabled(DataGridActionsMenuItems.SetAsPrimary)}
            >
              <ListItemIcon>
                {iconOverrides && iconOverrides[DataGridActionsMenuItems.SetAsPrimary] ? (
                  iconOverrides[DataGridActionsMenuItems.SetAsPrimary]
                ) : (
                  <CheckCircleIcon fontSize='small' />
                )}
              </ListItemIcon>
              <ListItemText>
                {buttonTitles && buttonTitles[DataGridActionsMenuItems.SetAsPrimary]
                  ? buttonTitles[DataGridActionsMenuItems.SetAsPrimary]
                  : t('buttonText.setAsPrimary')}
              </ListItemText>
            </MenuItem>
          </Tooltip>
        );
      }

      if (action === DataGridActionsMenuItems.EditPO) {
        return (
          <Tooltip {...tooltipProps} title={getTooltipTitle(DataGridActionsMenuItems.EditPO)} key={action}>
            <MenuItem
              data-testid={`${id}-edit-po-btn`}
              onClick={() => onClick(DataGridActionsMenuItems.EditPO)}
              disabled={isDisabled(DataGridActionsMenuItems.EditPO)}
            >
              <ListItemText>
                {buttonTitles && buttonTitles[DataGridActionsMenuItems.EditPO]
                  ? buttonTitles[DataGridActionsMenuItems.EditPO]
                  : t('buttonText.editPO')}
              </ListItemText>
            </MenuItem>
          </Tooltip>
        );
      }

      if (action === DataGridActionsMenuItems.MarkAsComplete) {
        return (
          <Tooltip {...tooltipProps} title={getTooltipTitle(DataGridActionsMenuItems.MarkAsComplete)} key={action}>
            <MenuItem
              data-testid={`${id}-mark-as-complete-btn`}
              onClick={() => onClick(DataGridActionsMenuItems.MarkAsComplete)}
              disabled={isDisabled(DataGridActionsMenuItems.MarkAsComplete)}
            >
              <ListItemIcon>
                {iconOverrides && iconOverrides[DataGridActionsMenuItems.MarkAsComplete] ? (
                  iconOverrides[DataGridActionsMenuItems.MarkAsComplete]
                ) : (
                  <AssignmentTurnedIn fontSize='small' />
                )}
              </ListItemIcon>
              <ListItemText>
                {buttonTitles && buttonTitles[DataGridActionsMenuItems.MarkAsComplete]
                  ? buttonTitles[DataGridActionsMenuItems.MarkAsComplete]
                  : t('buttonText.markAsComplete')}
              </ListItemText>
            </MenuItem>
          </Tooltip>
        );
      }

      return null;
    });

  return (
    <>
      <IconButton
        id='actions'
        aria-label='actions'
        aria-controls={open ? 'actions-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup='true'
        onClick={handleClick}
        data-testid='actions-menu-button'
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id='actions-menu'
        anchorEl={anchorEl}
        MenuListProps={{
          'aria-labelledby': 'actions',
        }}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {actionOptions}
      </Menu>
    </>
  );
}

CustomActionsMenuCell.defaultProps = {
  onClickFunctions: undefined,
  buttonTitles: undefined,
  tooltipTitles: undefined,
  iconOverrides: undefined,
  isActive: false,
  extraPermissions: undefined,
  disabled: false,
  id: '',
};

export default CustomActionsMenuCell;
